import * as React from 'react';
import { FunctionComponent, useEffect, useState } from 'react';
import classNames from 'classnames';
import { getUserTagColor } from 'Src/helpers/tags/tagColors';
import '../UserTagAssignModal/UserTagActionModal.scss';
import { UnsavedChangesModal } from '../UnsavedChangesModal/UnsavedChangesModal';

interface Props {
  modalTitle: string;
  userTags: any[];
  selectedUserTagId: string;
  selectedUserTagIds?: string[];
  onUserTagChange: (event) => void;
  onCloseClick: () => void;
  isOpen: boolean;
  onDeleteClick: (selectedTagIds?: string[]) => void;
}

export const UserTagDeleteModal: FunctionComponent<Props> = props => {
  const [isDiscardWarningOpen, setIsDiscardWarningOpen] = useState(false);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  useEffect(() => {
    if (props.isOpen) setSelectedIds(props.selectedUserTagIds || (props.selectedUserTagId ? [props.selectedUserTagId] : []));
  }, [props.isOpen]);
  if (!props.isOpen) {
    return null;
  }

  const modalClasses = classNames('userTagActionModal', {
    mDisplayBlock: props.isOpen,
    show: props.isOpen,
    modal: true,
    fade: true
  });

  const onPick = (tagId: string) => setSelectedIds(current => current.includes(tagId) ? current.filter(id => id !== tagId) : [...current, tagId]);
  const requestClose = () => (selectedIds.length ? setIsDiscardWarningOpen(true) : props.onCloseClick());
  const discardChanges = () => {
    setIsDiscardWarningOpen(false);
    props.onCloseClick();
  };
  const selectedTags = props.userTags.filter(tag => selectedIds.includes(tag.id));
  const availableTags = props.userTags.filter(tag => !selectedIds.includes(tag.id));

  return (
    <div className={modalClasses} onMouseDown={event => event.target === event.currentTarget && requestClose()}>
      <div className="modal-dialog modal-lg">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">{props.modalTitle}</h5>
            <button type="button" className="close" onClick={requestClose}>
              <span>&times;</span>
            </button>
          </div>
          <div className="modal-body">
            <div className="userTagAction">
              <p className="userTagAction__hint">Choose a tag to remove from the selected students.</p>
              {selectedTags.length > 0 && <div className="userTagAction__selection"><span className="userTagAction__label">Selected for removal</span><div className="userTagAction__chips userTagAction__chips--selected">
                {selectedTags.map(tag => <button key={tag.id} type="button" className="userTagAction__chip userTagAction__chip--remove" data-selected="true" onClick={() => onPick(tag.id)}><span className="userTagAction__dot" />{tag.tagName}<span aria-hidden="true">×</span></button>)}
              </div></div>}
              <span className="userTagAction__label">Available tags</span>
              {availableTags.length ? (
                <div className="userTagAction__chips">
                  {availableTags.map(tag => {
                    const color = getUserTagColor(tag);
                    return (
                      <button
                        key={tag.id}
                        type="button"
                        className="userTagAction__chip"
                        data-selected="false"
                        style={{ color, backgroundColor: `${color}1A` }}
                        onClick={() => onPick(tag.id)}
                      >
                        <span className="userTagAction__dot" />
                        {tag.tagName}
                      </button>
                    );
                  })}
                </div>
              ) : (
                <div className="userTagAction__empty">None of the selected students have tags yet.</div>
              )}
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="sit-btn sit-btn--ghost" onClick={requestClose}>
              Cancel
            </button>
            <button
              type="button"
              className="sit-btn sit-btn--danger"
              disabled={selectedIds.length === 0}
              onClick={() => props.onDeleteClick(selectedIds)}
            >
              Remove {selectedIds.length > 1 ? `${selectedIds.length} tags` : 'tag'}
            </button>
          </div>
        </div>
      </div>
      <UnsavedChangesModal
        isOpen={isDiscardWarningOpen}
        message="Your tag removal selection has not been saved. If you close now, that change will be lost."
        onKeepEditing={() => setIsDiscardWarningOpen(false)}
        onDiscard={discardChanges}
      />
    </div>
  );
};

UserTagDeleteModal.displayName = 'UserTagDeleteModal';
